import type { NumericFieldResult } from './types/numeric-field-result'

/** Shown for anything that isn't a finite number. Matches the Type panel's own copy. */
export const NUMERIC_FIELD_INVALID_MESSAGE = 'Invalid value'

/**
 * Range message. AC-6's screenshots read `Value: -100 to 200`; the Type panel currently emits
 * `Value: -100 and 200`. Following the spec here — the Type panel's copy is a separate one-word
 * fix, not something to fork over.
 */
export function numericFieldRangeMessage(min: number, max: number): string {
  return `Value: ${min} to ${max}`
}

/** Narrowing helper so callers don't repeat the `'error' in result` shape. */
export function isNumericFieldError(result: NumericFieldResult): result is { error: string } {
  return 'error' in result
}

/**
 * Parses `raw` as a number in `[min, max]`.
 *
 * An empty/whitespace-only string is an error, not a zero: the fields these back are always
 * populated, so a cleared field is mid-edit, and treating it as 0 would jump the preview while the
 * user is still typing. Callers hold the raw string in their own state and only commit on a valid
 * result, so nothing is lost while the field is empty.
 */
export function validateNumericField(raw: string, min: number, max: number): NumericFieldResult {
  const trimmed = raw.trim()

  if (trimmed.length === 0) return { error: NUMERIC_FIELD_INVALID_MESSAGE }

  const parsed = Number(trimmed)

  if (!Number.isFinite(parsed)) return { error: NUMERIC_FIELD_INVALID_MESSAGE }
  if (parsed < min || parsed > max) return { error: numericFieldRangeMessage(min, max) }

  return { value: parsed }
}
